import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import API from "../../../../services/api";
import ExtraWorkConfigLink from "../../../../components/settings/ExtraWorkConfigLink";
import WeeklyOffBudgetPanel from "../../../../components/settings/WeeklyOffBudgetPanel";
import { Banner, StickyBar, WizField } from "./shared";
import "../../../../styles/settingsHub.css";

const CYCLES = [
  { value: "monthly", label: "Monthly" },
  { value: "biweekly", label: "Bi-Weekly" },
  { value: "weekly", label: "Weekly" },
];

const ATTENDANCE_CYCLES = [
  { value: "calendar_month", label: "Calendar month (1st – last day)" },
  { value: "salary_cycle", label: "Aligned to payroll / salary cycle" },
  { value: "custom_period", label: "Custom attendance period" },
];

function normalizeCycle(v) {
  const s = String(v || "monthly").toLowerCase();
  if (s === "biweekly" || s === "bi-weekly") return "biweekly";
  if (s === "weekly") return "weekly";
  return "monthly";
}

export default function StepPayrollSettings({ canEdit, onSaved, refetchSetupProgress, toast }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [form, setForm] = useState({
    default_structure: "Standard",
    payroll_frequency: "monthly",
    attendance_cycle: "calendar_month",
    salary_payment_day: 10,
    payroll_processing_day: 25,
    monthly_salary_divisor_days: 26,
    lop_enabled: true,
    prorate_with_attendance: true,
    payable_days_mode: "attendance_units",
    fuel_reimbursement_enabled: false,
  });
  const [errors, setErrors] = useState({});
  const refs = {
    default_structure: useRef(null),
    payroll_frequency: useRef(null),
    attendance_cycle: useRef(null),
    salary_payment_day: useRef(null),
    payroll_processing_day: useRef(null),
    monthly_salary_divisor_days: useRef(null),
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await API.get("/organisation/me/hr-settings");
        if (cancelled) return;
        const hr = res.data?.hr_settings || {};
        const sub = res.data?.payroll_settings || hr.payroll || {};
        setForm({
          default_structure: sub.default_structure || "Standard",
          payroll_frequency: normalizeCycle(hr.salary_cycle || sub.salary_cycle || sub.payroll_frequency),
          attendance_cycle: sub.attendance_cycle || "calendar_month",
          salary_payment_day: hr.salary_payment_day ?? sub.salary_payment_day ?? 10,
          payroll_processing_day: hr.payroll_processing_day ?? sub.payroll_processing_day ?? 25,
          monthly_salary_divisor_days: hr.monthly_salary_divisor_days ?? sub.monthly_salary_divisor_days ?? 26,
          lop_enabled: sub.lop_enabled !== false,
          prorate_with_attendance: sub.prorate_with_attendance !== false,
          payable_days_mode: sub.payable_days_mode || "attendance_units",
          fuel_reimbursement_enabled: Boolean(sub.fuel_reimbursement_enabled),
        });
      } catch {
        /* keep defaults */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => (e[k] ? { ...e, [k]: undefined } : e));
  };

  function validate() {
    const e = {};
    const day = (v) => Number.isInteger(Number(v)) && Number(v) >= 1 && Number(v) <= 31;
    if (!String(form.default_structure || "").trim()) e.default_structure = "Default salary structure name is required.";
    if (!["monthly", "biweekly", "weekly"].includes(form.payroll_frequency)) {
      e.payroll_frequency = "Payroll Frequency is required.";
    }
    if (!form.attendance_cycle) e.attendance_cycle = "Attendance Cycle is required.";
    if (!day(form.salary_payment_day)) e.salary_payment_day = "Salary payment day must be between 1 and 31.";
    if (!day(form.payroll_processing_day)) e.payroll_processing_day = "Payroll processing day must be between 1 and 31.";
    if (!day(form.monthly_salary_divisor_days)) {
      e.monthly_salary_divisor_days = "Working days (divisor) must be between 1 and 31.";
    }
    return e;
  }

  function focusFirstInvalid(e) {
    const order = [
      "default_structure",
      "payroll_frequency",
      "attendance_cycle",
      "salary_payment_day",
      "payroll_processing_day",
      "monthly_salary_divisor_days",
    ];
    const first = order.find((k) => e[k]);
    const wrap = first && refs[first]?.current;
    if (wrap) {
      wrap.scrollIntoView({ behavior: "smooth", block: "center" });
      const control = wrap.querySelector("input, select");
      setTimeout(() => (control || wrap).focus?.(), 300);
    }
  }

  async function onSaveContinue() {
    if (!canEdit) {
      toast.error("You do not have permission to save payroll settings.");
      return;
    }
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length > 0) {
      focusFirstInvalid(e);
      return;
    }
    try {
      setSaving(true);
      const salary_cycle = form.payroll_frequency;
      const payload = {
        salary_cycle,
        salary_payment_day: Number(form.salary_payment_day),
        payroll_processing_day: Number(form.payroll_processing_day),
        monthly_salary_divisor_days: Number(form.monthly_salary_divisor_days),
        payroll: {
          salary_cycle,
          payroll_frequency: salary_cycle,
          attendance_cycle: form.attendance_cycle,
          salary_payment_day: Number(form.salary_payment_day),
          payroll_processing_day: Number(form.payroll_processing_day),
          monthly_salary_divisor_days: Number(form.monthly_salary_divisor_days),
          default_structure: form.default_structure,
          lop_enabled: Boolean(form.lop_enabled),
          total_working_days_mode: "fixed",
          payable_days_mode: form.payable_days_mode || "attendance_units",
          prorate_with_attendance: Boolean(form.prorate_with_attendance),
          fuel_reimbursement_enabled: Boolean(form.fuel_reimbursement_enabled),
          settings_completed: true,
          settings_saved_at: new Date().toISOString(),
        },
      };
      await API.patch("/organisation/me/hr-settings", payload);
      await refetchSetupProgress?.();
      setSaved(true);
      toast.success("✓ Payroll Settings Saved Successfully");
      if (typeof onSaved === "function") {
        setTimeout(() => onSaved(), 900);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to save payroll settings");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <p className="ps-wiz-loading">
        <Loader2 className="ps-spin" size={16} /> Loading settings…
      </p>
    );
  }

  return (
    <div>
      <p className="ps-wiz-help">
        Configure how and when payroll runs — frequency, attendance cycle, and LOP.
        Fields marked <span className="ps-wiz-req">*</span> are required.
      </p>

      {saved ? (
        <Banner tone="success">✓ Payroll Settings Saved Successfully</Banner>
      ) : null}

      <div className="ps-wiz-section-label">Payroll cycle &amp; schedule</div>
      <div className="ps-wiz-grid">
        <WizField label="Default salary structure" required refWrap={refs.default_structure} error={errors.default_structure}>
          <input
            className={`ps-wiz-input${errors.default_structure ? " ps-wiz-input--err" : ""}`}
            value={form.default_structure}
            disabled={!canEdit}
            onChange={(ev) => set("default_structure", ev.target.value)}
            placeholder="e.g. Standard"
          />
        </WizField>

        <WizField
          label="Payroll Frequency"
          required
          refWrap={refs.payroll_frequency}
          error={errors.payroll_frequency}
          hint="How often salaries are processed"
        >
          <select
            className={`ps-wiz-input${errors.payroll_frequency ? " ps-wiz-input--err" : ""}`}
            value={form.payroll_frequency}
            disabled={!canEdit}
            onChange={(ev) => set("payroll_frequency", ev.target.value)}
          >
            {CYCLES.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </WizField>

        <WizField
          label="Attendance Cycle"
          required
          refWrap={refs.attendance_cycle}
          error={errors.attendance_cycle}
          hint="Period used to compute attendance / payable days for payroll"
        >
          <select
            className={`ps-wiz-input${errors.attendance_cycle ? " ps-wiz-input--err" : ""}`}
            value={form.attendance_cycle}
            disabled={!canEdit}
            onChange={(ev) => set("attendance_cycle", ev.target.value)}
          >
            {ATTENDANCE_CYCLES.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </WizField>

        <WizField label="Salary payment day" required refWrap={refs.salary_payment_day} error={errors.salary_payment_day} hint="Day of month salary is paid (1–31)">
          <input
            type="number"
            min={1}
            max={31}
            className={`ps-wiz-input${errors.salary_payment_day ? " ps-wiz-input--err" : ""}`}
            value={form.salary_payment_day}
            disabled={!canEdit}
            onChange={(ev) => set("salary_payment_day", ev.target.value)}
          />
        </WizField>

        <WizField label="Payroll processing day" required refWrap={refs.payroll_processing_day} error={errors.payroll_processing_day} hint="Day payroll is processed each cycle (1–31)">
          <input
            type="number"
            min={1}
            max={31}
            className={`ps-wiz-input${errors.payroll_processing_day ? " ps-wiz-input--err" : ""}`}
            value={form.payroll_processing_day}
            disabled={!canEdit}
            onChange={(ev) => set("payroll_processing_day", ev.target.value)}
          />
        </WizField>

        <WizField label="Payable days (salary divisor)" required refWrap={refs.monthly_salary_divisor_days} error={errors.monthly_salary_divisor_days} hint="Fixed for every month. Weekly offs use only the days left in that month.">
          <input
            type="number"
            min={1}
            max={31}
            className={`ps-wiz-input${errors.monthly_salary_divisor_days ? " ps-wiz-input--err" : ""}`}
            value={form.monthly_salary_divisor_days}
            disabled={!canEdit}
            onChange={(ev) => set("monthly_salary_divisor_days", ev.target.value)}
          />
        </WizField>
      </div>

      <div style={{ marginTop: 12 }}>
        <WeeklyOffBudgetPanel workingDays={form.monthly_salary_divisor_days} showSettingsLink={false} />
      </div>

      <div className="ps-wiz-section-label" style={{ marginTop: "1.25rem" }}>LOP configuration</div>
      <div className="ps-wiz-taxrow">
        <button
          type="button"
          disabled={!canEdit}
          className={`ps-wiz-toggle${form.lop_enabled ? " on" : ""}`}
          onClick={() => set("lop_enabled", !form.lop_enabled)}
        >
          <span className="ps-wiz-toggle-track"><span className="ps-wiz-toggle-thumb" /></span>
          Deduct LOP (Loss of Pay)
        </button>
        <button
          type="button"
          disabled={!canEdit}
          className={`ps-wiz-toggle${form.prorate_with_attendance ? " on" : ""}`}
          onClick={() => set("prorate_with_attendance", !form.prorate_with_attendance)}
        >
          <span className="ps-wiz-toggle-track"><span className="ps-wiz-toggle-thumb" /></span>
          Prorate earnings with attendance
        </button>
      </div>
      <p className="ps-wiz-hint" style={{ marginTop: 8 }}>
        When LOP is enabled, unpaid absence days reduce payable days and prorated earnings automatically.
      </p>

      <div className="ps-wiz-section-label" style={{ marginTop: "1.25rem" }}>Fuel Reimbursement</div>
      <div className="ps-wiz-taxrow">
        <button
          type="button"
          disabled={!canEdit}
          className={`ps-wiz-toggle${form.fuel_reimbursement_enabled ? " on" : ""}`}
          onClick={() => set("fuel_reimbursement_enabled", !form.fuel_reimbursement_enabled)}
          aria-pressed={Boolean(form.fuel_reimbursement_enabled)}
        >
          <span className="ps-wiz-toggle-track"><span className="ps-wiz-toggle-thumb" /></span>
          Fuel Reimbursement: {form.fuel_reimbursement_enabled ? "ON" : "OFF"}
        </button>
      </div>
      <p className="ps-wiz-hint" style={{ marginTop: 8 }}>
        When ON, HR can mark employees as eligible and pay approved petrol bills in that payroll month. This is not a fixed salary allowance. When OFF, fuel bills and payroll fuel lines stay hidden.
      </p>

      <div className="ps-wiz-section-label" style={{ marginTop: "1.25rem" }}>Extra work compensation</div>
      <ExtraWorkConfigLink />

      <StickyBar>
        <span className="ps-wiz-spacer" />
        <button type="button" className="ps-wiz-btn ps-wiz-btn--primary" onClick={onSaveContinue} disabled={saving}>
          {saving ? (
            <><Loader2 className="ps-spin" size={15} /> Saving…</>
          ) : (
            <>Save settings</>
          )}
        </button>
      </StickyBar>
    </div>
  );
}
